/**
 * 六向标准视图（P0-3d B-1）：前/后/左/右/顶/底 六个正交视角的位姿解算——纯函数、可离线测。
 *
 * 与机位预设（cameraPresets）的区别：预设是"分镜构图"（带仰角/距离/瞄准高度语义），
 * 六向视图是"从六个正交方向平视场景"的建模辅助视角（对齐 DCC 的前视/顶视等），
 * 用于快速把自由视角摆到某个正方向，不参与工程数据、不建机位。
 *
 * 命名按"相机所在方向"：front=相机在 +Z（看角色正面，与 yaw0 约定一致）。
 * top/bottom 加极小偏移，避免相机恰在极点导致 OrbitControls 万向锁（up 与视线平行）。
 */
import type { Vec3 } from '../../model/director';

export type StdView = 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom';

export const STD_VIEWS: ReadonlyArray<{ id: StdView; label: string }> = [
  { id: 'front', label: '前' },
  { id: 'back', label: '后' },
  { id: 'left', label: '左' },
  { id: 'right', label: '右' },
  { id: 'top', label: '顶' },
  { id: 'bottom', label: '底' },
];

/** 顶/底视角的极小偏移，防 OrbitControls 在极点万向锁 */
const POLE_EPS = 0.0001;

/** 单位方向（相机相对注视点的方位） */
export function stdViewOffset(dir: StdView): Vec3 {
  switch (dir) {
    case 'front':
      return [0, 0, 1];
    case 'back':
      return [0, 0, -1];
    case 'left':
      return [-1, 0, 0];
    case 'right':
      return [1, 0, 0];
    case 'top':
      return [0, 1, POLE_EPS];
    case 'bottom':
      return [0, -1, POLE_EPS];
  }
}

export interface ViewPose {
  position: Vec3;
  target: Vec3;
}

/** 由注视中心 + 距离 + 方向解算相机位姿（纯函数） */
export function stdViewPose(center: Vec3, dist: number, dir: StdView): ViewPose {
  const o = stdViewOffset(dir);
  return {
    position: [center[0] + o[0] * dist, center[1] + o[1] * dist, center[2] + o[2] * dist],
    target: [center[0], center[1], center[2]],
  };
}

/** 六向注视点所需的对象最小面（避免依赖完整 DirectorObject 类型） */
export interface Centerable {
  id: string;
  visible: boolean;
  uniformScale: number;
  transform: { position: Vec3 };
}

/**
 * 六向视图的注视中心（纯函数，修“偏右”）：
 *  · 选中单个对象 → 以它（胸高）为中心；
 *  · 否则 → 以所有可见对象的质心为中心（而非固定的场景原点——
 *    角色常排在正 X 方向，若钉在原点看会整体偏向一侧）；无对象回退 [0,1,0]。
 */
export function viewCenter(objects: readonly Centerable[], selectedId: string | null): Vec3 {
  if (selectedId !== null) {
    const sel = objects.find((o) => o.id === selectedId);
    if (sel !== undefined) {
      const p = sel.transform.position;
      return [p[0], p[1] + 0.9 * sel.uniformScale, p[2]];
    }
  }
  const vis = objects.filter((o) => o.visible);
  const pool = vis.length > 0 ? vis : objects;
  if (pool.length === 0) {
    return [0, 1, 0];
  }
  let x = 0;
  let y = 0;
  let z = 0;
  for (const o of pool) {
    x += o.transform.position[0];
    y += o.transform.position[1];
    z += o.transform.position[2];
  }
  const n = pool.length;
  return [x / n, y / n + 0.9, z / n];
}
